import { CheckSquareIcon } from '@phosphor-icons/react'
import { NavArrowDown, NavArrowLeft, NavArrowRight, NavArrowUp, Plus } from 'iconoir-react'
import moment from 'moment'
import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ContactDrawer } from 'app/components/contacts/ContactDrawer'
import { TaskCalendarChip } from 'app/components/tasks/TaskCalendarChip'
import { TaskDetailSheet } from 'app/components/tasks/TaskDetailSheet'
import { showQuickCreateTask } from 'app/lib/quickCreateTask'
import { TaskWeekStrip } from 'app/components/tasks/TaskWeekStrip'
import { useCalendarTasks } from 'app/components/tasks/useCalendarTasks'
import { Button } from 'app/components/ui/button'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { DAY_KEY, DUE_DAY_PHRASES, tasksByDay, visibleRange } from 'lib/taskCalendar'
import { cn } from 'app/lib/utils'
import { notifyError } from 'app/services/notify.client'
import type { ContactJson, TaskJson } from 'app/types/entities'
import { useUser, useWorkspace } from 'app/utils'
import { bg } from 'lib/background'
import { myRoleId, myTasksSearch } from 'lib/taskAssignee'
import { readLocationIds } from './locationParam'
import { FAMILY_ACCENT, FAMILY_TINT } from './queueRegistry'

// Overdue reaches back to the first task anyone could have; the API has no open-ended range.
const EARLIEST_DUE = moment(`2000-01-01`)

/** Tasks assigned to me or my role, the Tasks page's My Tasks search; overdue is pinned above the day. */
export function MyTasksCard() {
  const user = useUser()
  const workspace = useWorkspace()
  const [searchParams] = useSearchParams()
  const [selectedDay, setSelectedDay] = useState(() => moment().startOf(`day`))
  const [showOverdue, setShowOverdue] = useState(false)
  const [selectedTask, setSelectedTask] = useState<{ id: string; overdue: boolean }>()
  const [openContact, setOpenContact] = useState<ContactJson>()
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  // The page's Locations control encodes into `filters`; the card obeys it
  // like every other figure on the page.
  const filtersParam = searchParams.get(`filters`)
  const scope = useMemo(() => {
    const locationIds = readLocationIds(filtersParam)
    const mine = myTasksSearch(user?.id ?? ``, user ? myRoleId(user, workspace?.id) : undefined)
    return locationIds.length ? `${mine} AND locationId:(${locationIds.map((id) => `"${id}"`).join(` OR `)})` : mine
  }, [user, workspace?.id, filtersParam])

  const week = useCalendarTasks({
    query: scope,
    range: visibleRange(`week`, selectedDay),
    refreshTrigger,
    onTaskUpdated: () => setRefreshTrigger((n) => n + 1),
  })
  const overdue = useCalendarTasks({
    query: `${scope} AND status.keyword:"open"`,
    range: { start: EARLIEST_DUE, end: moment().startOf(`day`).subtract(1, `day`) },
    refreshTrigger,
    onTaskUpdated: () => setRefreshTrigger((n) => n + 1),
  })

  const byDay = useMemo(() => tasksByDay(week.tasks), [week.tasks])
  const dayTasks = byDay.get(selectedDay.format(DAY_KEY)) ?? []
  // A task completed here stays listed, struck through, so the count is of what is still open.
  const overdueOpen = overdue.tasks.filter((t) => t.status === `open`).length
  const owner = selectedTask?.overdue ? overdue : week
  const openTask = owner.tasks.find((t) => t.id === selectedTask?.id)

  const openContactFor = (task: TaskJson) => {
    if (!task.contactId) return
    bg(async () => {
      const { hits } = await gqlFetchList<ContactJson>(`contacts`, {
        search: `id:"${task.contactId}"`,
        pagination: { page: 0, limit: 1 },
      })
      if (hits?.[0]) setOpenContact(hits[0])
      else notifyError(`Contact not found`)
    })
  }

  const chip = (task: TaskJson, fromOverdue: boolean) => (
    <TaskCalendarChip
      key={task.id}
      task={task}
      variant="compact"
      onOpen={() => task.id && setSelectedTask({ id: task.id, overdue: fromOverdue })}
      onToggleStatus={() => (fromOverdue ? overdue : week).toggleStatus(task)}
    />
  )

  const calendarHref = `/tasks?tab=my-tasks&view=calendar&cal=week&day=${selectedDay.format(DAY_KEY)}`

  return (
    <div
      className={cn(
        `flex min-h-96 flex-col overflow-hidden rounded-xl border border-default bg-surface bg-linear-to-b to-transparent to-60% p-6 shadow-elevation-2 transition hover:shadow-elevation-4`,
        FAMILY_TINT.lead,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className={cn(`rounded-lg p-2.5`, FAMILY_ACCENT.lead)}>
            <CheckSquareIcon size={22} weight="bold" />
          </div>
          <div className="min-w-0">
            <p className="text-ui-lg text-strong">My tasks</p>
            <p className="hidden text-body-xs text-subtle md:block">Assigned to you or your role</p>
          </div>
        </div>
        <Link to="/tasks?tab=my-tasks" className="shrink-0 text-body-xs text-subtle hover:text-default hover:underline">
          View all
        </Link>
      </div>

      <div className="mt-4 flex items-center gap-1">
        <Button
          variant="subtle"
          size="icon-sm"
          aria-label="Previous week"
          onClick={() => setSelectedDay((day) => day.clone().subtract(1, `week`))}
        >
          <NavArrowLeft />
        </Button>
        <div className="min-w-0 flex-1">
          <TaskWeekStrip anchor={selectedDay} tasksByDay={byDay} onOpenDay={(day) => setSelectedDay(day.clone())} />
        </div>
        <Button
          variant="subtle"
          size="icon-sm"
          aria-label="Next week"
          onClick={() => setSelectedDay((day) => day.clone().add(1, `week`))}
        >
          <NavArrowRight />
        </Button>
      </div>

      {/* Size containment keeps a long task list from growing the card: it
          stretches to its grid row and the list scrolls in whatever is left. */}
      <div className="mt-3 flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto border-t border-subtle pt-3 [contain:size]">
        {overdue.tasks.length > 0 && (
          <>
            <Button variant="ghost" size="row" aria-expanded={showOverdue} onClick={() => setShowOverdue((v) => !v)}>
              <span className="flex w-full items-center justify-between text-body-xs">
                <span className="font-medium text-red-600">{overdueOpen} overdue</span>
                {showOverdue ? <NavArrowUp /> : <NavArrowDown />}
              </span>
            </Button>
            {showOverdue && overdue.tasks.map((task) => chip(task, true))}
            <div className="my-1 border-t border-subtle" />
          </>
        )}
        <p className="px-2 text-caption-sm font-medium uppercase tracking-wide text-subtle">
          {selectedDay.format(`dddd, MMM D`)}
        </p>
        {week.isLoading ? (
          <div className="mx-2 h-5 animate-pulse rounded bg-surface-muted" />
        ) : dayTasks.length === 0 ? (
          <p className="px-2 py-1 text-body-sm text-subtle">
            Nothing due {selectedDay.calendar(null, DUE_DAY_PHRASES)}.
          </p>
        ) : (
          dayTasks.map((task) => chip(task, false))
        )}
      </div>

      <div className="mt-4 flex items-center justify-between border-t border-subtle pt-4">
        <Button
          variant="secondary"
          size="sm"
          onClick={() =>
            showQuickCreateTask({
              defaultDueDate: selectedDay.toDate(),
              onCreated: () => setRefreshTrigger((n) => n + 1),
            })
          }
        >
          <Plus />
          New Task
        </Button>
        <Link to={calendarHref} className="text-body-xs text-subtle hover:text-default hover:underline">
          Open calendar
        </Link>
      </div>

      <TaskDetailSheet
        task={openTask}
        onClose={() => setSelectedTask(undefined)}
        onToggleStatus={owner.toggleStatus}
        onUpdate={owner.updateTaskFields}
        onDelete={owner.deleteTask}
        onOpenContact={openContactFor}
        onTaskCreated={() => setRefreshTrigger((n) => n + 1)}
      />
      <ContactDrawer
        openDrawer={Boolean(openContact)}
        contact={openContact}
        onClose={() => setOpenContact(undefined)}
        onChange={setOpenContact}
      />
    </div>
  )
}
